import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import AppsIcon from '@mui/icons-material/Apps';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useState, type ReactNode } from 'react';
import { NavLink, Outlet } from 'react-router-dom';

import { isFeatureEnabled } from '@/config/environment';
import { useManagerSession } from '@/features/auth/hooks/useManagerSession';

const headerHeight = 48;
const navigationWidth = 192;
const collapsedNavigationWidth = 48;
const navigationPreferenceKey = 'poolappy-admin.navigation-collapsed';

interface NavigationItem {
  label: string;
  to: string;
  icon: ReactNode;
  permission?: string;
  feature?: string;
}

const navigationGroups: { label: string; items: NavigationItem[] }[] = [
  {
    label: 'Overview',
    items: [
      { label: 'Dashboard', to: '/foundation', icon: <DashboardOutlinedIcon /> },
      {
        label: 'Calendar',
        to: '/calendar',
        icon: <CalendarMonthOutlinedIcon />,
        feature: 'calendar',
      },
      { label: 'Map', to: '/map', icon: <MapOutlinedIcon />, feature: 'map' },
    ],
  },
  {
    label: 'Customers',
    items: [
      {
        label: 'Clients',
        to: '/clients',
        icon: <PeopleAltOutlinedIcon />,
        permission: 'view clients',
        feature: 'clients',
      },
      {
        label: 'Service reports',
        to: '/reports',
        icon: <Inventory2OutlinedIcon />,
        permission: 'view service reports',
        feature: 'reports',
      },
    ],
  },
  {
    label: 'Workforce',
    items: [
      {
        label: 'Technicians',
        to: '/technicians',
        icon: <PersonOutlineIcon />,
        permission: 'view technicians',
        feature: 'people',
      },
      {
        label: 'Managers',
        to: '/managers',
        icon: <AccountCircleOutlinedIcon />,
        feature: 'people',
      },
    ],
  },
  {
    label: 'Operations',
    items: [
      {
        label: 'Shopping list',
        to: '/shopping-list',
        icon: <Inventory2OutlinedIcon />,
        permission: 'view shopping list items',
        feature: 'shopping',
      },
      {
        label: 'Groups & SMS',
        to: '/communications',
        icon: <SmsOutlinedIcon />,
        feature: 'communications',
      },
      { label: 'Settings', to: '/settings', icon: <SettingsOutlinedIcon />, feature: 'settings' },
    ],
  },
];

export function AdminShell() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('md'));
  const [mobileNavigationOpen, setMobileNavigationOpen] = useState(false);
  const [navigationCollapsed, setNavigationCollapsed] = useState(() =>
    typeof window === 'undefined'
      ? false
      : window.localStorage.getItem(navigationPreferenceKey) === 'true',
  );
  const session = useManagerSession();
  const collapsed = desktop && navigationCollapsed;
  const activeNavigationWidth = collapsed ? collapsedNavigationWidth : navigationWidth;

  const handleNavigationToggle = () => {
    if (!desktop) {
      setMobileNavigationOpen(true);
      return;
    }

    setNavigationCollapsed((current) => {
      const next = !current;
      window.localStorage.setItem(navigationPreferenceKey, String(next));
      return next;
    });
  };

  const navigation = (
    <Stack sx={{ height: '100%' }}>
      <Toolbar
        variant="dense"
        sx={{
          minHeight: 44,
          px: collapsed ? 0 : 1,
          justifyContent: collapsed ? 'center' : 'flex-start',
        }}
      >
        {desktop && (
          <Tooltip
            title={collapsed ? 'Expand navigation' : 'Collapse navigation'}
            placement="right"
          >
            <IconButton
              aria-label={collapsed ? 'Expand navigation' : 'Collapse navigation'}
              onClick={handleNavigationToggle}
              size="small"
            >
              <MenuIcon sx={{ fontSize: 18, shapeRendering: 'geometricPrecision' }} />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
      <Box
        component="nav"
        aria-label="Primary navigation"
        sx={{
          flex: 1,
          overflowY: 'auto',
          scrollbarWidth: 'none',
          py: 0.5,
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {navigationGroups.map((group) => {
          const items = group.items.filter((item) => canViewItem(item, session.user.permissions));
          if (items.length === 0) return null;

          return (
            <Box key={group.label} sx={{ mb: collapsed ? 0.5 : 1 }}>
              {collapsed ? (
                <Divider sx={{ mx: 1.5, my: 0.75 }} />
              ) : (
                <Typography
                  variant="caption"
                  component="h2"
                  sx={{
                    display: 'block',
                    px: 2,
                    py: 0.5,
                    fontWeight: 700,
                    color: 'text.secondary',
                  }}
                >
                  {group.label}
                </Typography>
              )}
              <List component="div" disablePadding>
                {items.map((item) => {
                  const navigationItem = (
                    <ListItemButton
                      key={item.to}
                      component={NavLink}
                      to={item.to}
                      aria-label={collapsed ? item.label : undefined}
                      onClick={() => setMobileNavigationOpen(false)}
                      sx={{
                        minHeight: 36,
                        px: collapsed ? 0 : 2,
                        justifyContent: collapsed ? 'center' : 'flex-start',
                        borderLeft: '3px solid transparent',
                        '&.active': {
                          bgcolor: 'common.white',
                          borderLeftColor: 'primary.main',
                        },
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: collapsed ? 0 : 32,
                          justifyContent: 'center',
                          color: '#424242',
                          '& .MuiSvgIcon-root': {
                            fontSize: 18,
                            shapeRendering: 'geometricPrecision',
                          },
                        }}
                      >
                        {item.icon}
                      </ListItemIcon>
                      {!collapsed && (
                        <ListItemText
                          primary={item.label}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      )}
                    </ListItemButton>
                  );

                  return collapsed ? (
                    <Tooltip key={item.to} title={item.label} placement="right" arrow>
                      {navigationItem}
                    </Tooltip>
                  ) : (
                    navigationItem
                  );
                })}
              </List>
            </Box>
          );
        })}
      </Box>
      <Divider />
      <Box sx={{ p: collapsed ? 1.25 : 2, textAlign: collapsed ? 'center' : 'left' }}>
        <Typography variant="body2" fontWeight={700} noWrap>
          {collapsed ? 'P' : 'PoolAppy'}
        </Typography>
        {!collapsed && (
          <Typography variant="caption" color="text.secondary">
            Operations
          </Typography>
        )}
      </Box>
    </Stack>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="fixed" elevation={0} sx={{ zIndex: theme.zIndex.drawer + 1 }}>
        <Toolbar variant="dense" sx={{ gap: 1.25, minHeight: headerHeight, px: 1.5 }}>
          <IconButton
            color="inherit"
            edge="start"
            aria-label="Open navigation"
            onClick={handleNavigationToggle}
            sx={{ display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Tooltip title="App launcher">
            <IconButton
              component={NavLink}
              to="/foundation"
              color="inherit"
              aria-label="PoolAppy home"
              size="small"
            >
              <AppsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Typography
            variant="subtitle1"
            component="div"
            sx={{ fontWeight: 600, letterSpacing: 0.1 }}
          >
            POOLAPPY
          </Typography>
          <Typography variant="body2">Operations</Typography>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={session.user.name}>
            <Box aria-label={`Account for ${session.user.name}`}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: 'transparent',
                  border: '1px solid white',
                  fontSize: 13,
                }}
              >
                {session.user.initials}
              </Avatar>
            </Box>
          </Tooltip>
        </Toolbar>
      </AppBar>
      <Drawer
        variant={desktop ? 'permanent' : 'temporary'}
        open={desktop || mobileNavigationOpen}
        onClose={() => setMobileNavigationOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          width: activeNavigationWidth,
          flexShrink: 0,
          transition: theme.transitions.create('width', {
            duration: theme.transitions.duration.shorter,
          }),
          '& .MuiDrawer-paper': {
            width: activeNavigationWidth,
            top: `${headerHeight}px`,
            height: `calc(100% - ${headerHeight}px)`,
            bgcolor: '#f3f2f1',
            borderRightColor: 'divider',
            overflowX: 'hidden',
            transition: theme.transitions.create('width', {
              duration: theme.transitions.duration.shorter,
            }),
          },
        }}
      >
        {navigation}
      </Drawer>
      <Box
        component="main"
        sx={{
          ml: { md: `${activeNavigationWidth}px` },
          pt: `${headerHeight}px`,
          minHeight: '100vh',
          transition: theme.transitions.create('margin-left', {
            duration: theme.transitions.duration.shorter,
          }),
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}

function canViewItem(item: NavigationItem, permissions: string[] | null): boolean {
  if (item.feature && !isFeatureEnabled(item.feature)) return false;
  if (item.permission === undefined || permissions === null) {
    return true;
  }

  return permissions.includes(item.permission);
}
